import { Checkbox } from '@mui/material';
import { CompanyRole } from 'constants/CompanyRole';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getContractCardsAction,
  reopenConfirmationAction,
  setContractCardToCopyAction,
  toggleCopyModeAction,
} from 'store/actions/contract-actions';
import { contractCardToCopySelector } from 'store/selectors/contracts-selector';
import { profileSelector } from 'store/selectors/profile-selectors';
import ContractCardDetails from './components/ContractCardDetails';
import ContractCardHeader from './components/ContractCardHeader';
import ContractCardActions from './components/actions/ContractCardActions';
import { isDateInFuture, isDisabledForModification } from './helpers';
import { ContractCardContainer, ContractCardItem, ContractCardOptions } from './styles';
import { ContractCardProps } from './types';

const ContractCard: React.FC<ContractCardProps> = ({
  card,
  onClickConfirm,
  isCopyEnabled,
  onDelete,
  onReopen,
  onEdit,
  year,
  week,
  companyId,
  onChangeCardCheck,
  onFillSlot,
  filters,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const { role } = useAppSelector(profileSelector);
  const contractCardToCopy = useAppSelector(contractCardToCopySelector);

  const [hoveredCardId, setHoveredCardId] = useState<number>(null);

  const [isAdmin, isEmployee] = [role === CompanyRole.ADMIN, role === CompanyRole.EMPLOYEE];
  const { isWeekly, isConfirmed, isSlot } = card;
  const isDashboard = location.pathname === '/dashboard';
  const cannotReopenContract = !isAdmin && isConfirmed;
  const isContractCardActive = card.isEditable && (!isEmployee || isAdmin);
  const areContractCardActionsShown =
    hoveredCardId === card.contractDateId ||
    (isCopyEnabled && Boolean(contractCardToCopy) && contractCardToCopy.contractDateId === card.contractDateId);

  const userCurrentCompanyId = useAppSelector(profileSelector).currentCompany;
  const selectedCompanyMatchesCard = card.companyId === +userCurrentCompanyId;

  const onClickEdit = () => onEdit(card.contractId);

  const onClickView = () => onEdit(card.contractId, true);

  const enableCopyMode = () => {
    dispatch(toggleCopyModeAction());
    dispatch(setContractCardToCopyAction(card));
  };

  const disableCopyMode = () => {
    dispatch(toggleCopyModeAction());
    dispatch(setContractCardToCopyAction(null));
  };

  const onClickCopy = () => {
    if (!isCopyEnabled) {
      enableCopyMode();
      return;
    }
    disableCopyMode();
  };

  const onClickDelete = () => {
    onDelete([card.contractDateId]);
  };

  const onClickReopen = () => {
    onReopen(card.contractDateId);
  };

  const onMouseEnterEvent = () => {
    if (isDashboard && isEmployee && !selectedCompanyMatchesCard) {
      return;
    }
    if (!isCopyEnabled) {
      setHoveredCardId(card.contractDateId);
    }
  };

  const onMouseLeaveEvent = () => {
    setHoveredCardId(null);
  };

  return (
    <ContractCardContainer
      isDashboardCard={isDashboard}
      onMouseEnter={onMouseEnterEvent}
      onMouseLeave={onMouseLeaveEvent}
    >
      <ContractCardItem
        active={isContractCardActive}
        hover={hoveredCardId === card.contractDateId}
        confirmed={card.isConfirmed}
        // confirmed={card.confirmHours}
      >
        <ContractCardHeader card={card} onChangeCardCheck={onChangeCardCheck} isEmployee={isEmployee} />
        <ContractCardDetails card={card} />
      </ContractCardItem>
      {areContractCardActionsShown && (
        <ContractCardOptions>
          {!isEmployee && !isDashboard && (
            <Checkbox
              checked={card.checked}
              disabled={isSlot}
              // disabled={!isSlot && card.employee.inactive}
              onChange={() => {
                onMouseLeaveEvent();
                onChangeCardCheck(card.contractId);
              }}
              sx={{ position: 'absolute', top: 0, right: -4 }}
            />
          )}
          <ContractCardActions
            cardId={card.contractDateId}
            isCopyEnabled={isCopyEnabled}
            isConfirmed={isConfirmed}
            isContractSlot={isSlot}
            isDashboard={isDashboard}
            isDisabled={isDisabledForModification(role, isWeekly, isDateInFuture(card.date.toString(), card.startHour))}
            isEditable={card.isEditable}
            isEmployee={isEmployee}
            cannotReopenContract={cannotReopenContract}
            onClickConfirm={onClickConfirm}
            onClickCopy={onClickCopy}
            onClickDelete={onClickDelete}
            onClickEdit={onClickEdit}
            onClickView={onClickView}
            onClickFill={() => onFillSlot(card.contractId)}
            onClickReopen={onClickReopen}
          />
        </ContractCardOptions>
      )}
    </ContractCardContainer>
  );
};

export default ContractCard;
